/* ==========================================================================
   H3O Timeline — scroll-driven saturation rail.
   - Progress (0..1) tracks the section's travel through the viewport and is
     written to the --h3o-p custom property (rAF-throttled).
   - The line fill scales with progress; a bolt marker travels along the rail
     with a live "Day N" pill (Day 1 -> Day 28+).
   - Steps flip .is-active as the marker passes their node.
   - prefers-reduced-motion: everything renders in its completed state.
   ========================================================================== */

if (!customElements.get('h3o-timeline-scroll')) {
  customElements.define(
    'h3o-timeline-scroll',
    class H3OTimelineScroll extends HTMLElement {
      connectedCallback() {
        this.steps = Array.from(this.querySelectorAll('.h3o-timeline__step'));
        this.dayPill = this.querySelector('[data-day-pill]');
        this.dayEnd = parseInt(this.dataset.dayEnd, 10) || 28;
        this.count = this.steps.length || 1;
        this.ticking = false;
        this.progress = -1;

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.apply(1);
          return;
        }

        this.onScroll = this.requestTick.bind(this);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        this.requestTick();
      }

      disconnectedCallback() {
        if (this.onScroll) {
          window.removeEventListener('scroll', this.onScroll);
          window.removeEventListener('resize', this.onScroll);
        }
      }

      requestTick() {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          this.ticking = false;
          this.update();
        });
      }

      update() {
        const rect = this.getBoundingClientRect();
        const vh = window.innerHeight || 1;
        /* start when the track's top passes 85% of the viewport,
           finish as its bottom approaches 45% — feels tied to reading pace */
        const total = rect.height + vh * 0.4;
        const travelled = vh * 0.85 - rect.top;
        const p = Math.min(1, Math.max(0, travelled / total));
        if (Math.abs(p - this.progress) < 0.002) return;
        this.apply(p);
      }

      apply(p) {
        this.progress = p;
        this.style.setProperty('--h3o-p', p.toFixed(4));

        /* activate steps as the marker reaches each node */
        this.steps.forEach((step, i) => {
          const threshold = this.count > 1 ? i / (this.count - 1) : 0;
          step.classList.toggle('is-active', p >= threshold - 0.02);
        });

        if (this.dayPill) {
          const day = Math.max(1, Math.round(1 + (this.dayEnd - 1) * p));
          this.dayPill.textContent = p >= 0.99 ? 'Day ' + this.dayEnd + '+' : 'Day ' + day;
        }
      }
    }
  );
}
